import { ProjectId } from 'api/models';
import { UserOption } from 'components/molecules/squad-picker';
import {
  MultiUserField,
  SingleUserField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { OptionGroup } from '../components/organisms/field-editors/components/controlled-select-based-editor-popover';
import { useInAndNotInProjectUsers } from './use-assignee-options';
import { useGetAllUsers } from './use-idb';
import { useTranslate } from './use-translation';

export const useUserFieldOptions = (
  {
    projectId,
    isPrivate = false,
  }: {
    projectId: ProjectId;
    isPrivate?: boolean;
  },
  field?: SingleUserField | MultiUserField,
): OptionGroup[] => {
  const translate = useTranslate();

  const {
    inProjectUsers: usersInProject,
    notInProjectUsers: usersNotInProject,
  } = useInAndNotInProjectUsers({
    projectId,
    isPrivate,
    includeDeactivatedUsers: false,
  });
  const allActiveUsers = useGetAllUsers({
    includeDeactivatedUsers: false,
  });

  if (!projectId[1] && field?.sourceType === SourceType.User) {
    const { users } = field.metaData;

    const options = users.non_external_user_ids.map((userId) => ({
      ...UserOption(users.by_user_id[userId]),
      value: userId,
    }));

    return [
      {
        label: '',
        options: options.sort(byLabel),
      },
    ];
  }
  if (!projectId[1] && field?.sourceType === SourceType.Task) {
    return [
      {
        label: translate('users'),
        options: allActiveUsers
          .map((u) => ({
            value: u.userId,
            label: fullName(u),
            imageUrl: u?.photoUrl,
            description: u.role?.roleName,
            color: getRandomColor(u.userId),
          }))
          .sort(byLabel),
      },
    ];
  }
  if (!projectId[1]) {
    return [
      {
        label: '',
        options: field?.metaData.options ?? [],
      },
    ];
  }

  return [
    {
      label: translate('users'),
      options: usersInProject
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
    {
      label: translate('not_in_project'),
      options: usersNotInProject
        .map((u) => ({
          value: u.userId,
          label: fullName(u),
          imageUrl: u.photoUrl,
          description: u.role?.roleName,
          color: getRandomColor(u.userId),
        }))
        .sort(byLabel),
    },
  ];
};
